import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TokenChipInput } from '../../components/ui/TokenChipInput';
import {
  accountTabLabel,
  resolveAccountTab,
  visibleEditTabIds,
  type AccountTabId,
} from './accountTabs';
import { centsToInputValue, inputValueToCents } from '../../lib/money';
import type {
  Account,
  AccountInput,
  AccountStatus,
  AccountType,
  Address,
  CommunityManagementConfig,
  ContentCreationConfig,
  Currency,
  DealershipAdsConfig,
  EscalationContacts,
  Industry,
  MediaSpendPaidBy,
  Service,
  ServiceChannelToggles,
  ServiceConfig,
} from '../../types/account';
import type { FranchiseProfile, FranchiseProfileInput } from '../../types/franchiseProfile';
import type {
  FormType,
  FranchiseLocationProfile,
  FranchiseLocationProfileInput,
} from '../../types/franchiseLocationProfile';
import { createAccount, listAccounts, listUsers, updateAccount } from './api';
import { ReportTemplateField } from './ReportTemplateField';
import type { ReportTemplate } from '../../types/reportTemplate';
import { seedAccount } from '../community';
import { accountManagerLabel } from '../../lib/accountManagerLabel';
import {
  createFranchiseLocationProfile,
  createFranchiseProfile,
  getFranchiseProfile,
  updateFranchiseLocationProfile,
  updateFranchiseProfile,
} from '../franchise/api';
import { renderTemplate } from '../../lib/templating';
import {
  MAX_BRAND_ANTI_EXAMPLES_CHARS,
  MAX_BRAND_VOICE_PROFILE_CHARS,
} from '../../lib/brandVoiceProfile';
import { BrandVoiceDrafter } from './BrandVoiceDrafter';
import { deriveRunFlagsFromServices } from '../../lib/platformEnabled';
import {
  SERVICE_VALUES,
  escalationRoleLabel,
  escalationRolesForAccountType,
  normalizeContentCreationConfig,
  serviceLabel,
  type EscalationRole,
} from '../../lib/accountServices';
import {
  describeSharedClusters,
  describeSharedOn,
  shouldShowMatchTokens,
} from '../../lib/adAccountSharing';
import { getLeadForms, getPixels } from '../metaAds';
import { listContacts } from '../contacts';
import { escalationContactOptions } from './escalationContactOptions';
import { ACCOUNT_LINKS_QUERY_KEY } from '../../lib/useAccountLinks';
import { BrandDriveSettings } from './BrandDriveSettings';
import { parseVerifiedAdvertiserId, VERIFIED_ADVERTISER_ID_MESSAGE } from '../../lib/metaRegionalRegulation';

type Mode =
  | { kind: 'create' }
  | {
      kind: 'edit';
      account: Account;
      franchiseProfile: FranchiseProfile | null;
      franchiseLocationProfile: FranchiseLocationProfile | null;
    };

const FORM_TYPES: FormType[] = ['Generic', 'Qualifying Question'];

// Pre-P2b-3 polish round 5 §1: location-level Meta ad type default.
// Tri-state ("no default" + the two terminal types). Maps to null on the
// wire when 'no-default' is selected (lpToInput handles the conversion).
const DEFAULT_META_AD_TYPES: Array<'no-default' | 'traffic' | 'lead_gen'> = [
  'no-default',
  'traffic',
  'lead_gen',
];
const DEFAULT_META_AD_TYPE_LABEL: Record<'no-default' | 'traffic' | 'lead_gen', string> = {
  'no-default': 'No default',
  traffic: 'Website Lead',
  lead_gen: 'Meta Lead Gen',
};

// Shown below every template-bearing input so admins don't have to remember
// which placeholders are supported. Case-sensitive; see spec §5.
const PLACEHOLDERS = '{accountName} {suburb} {shortSuburb} {suburbSlug} {slug} {state}';

function PlaceholderHint() {
  return (
    <p className="text-xs text-slate-500 mt-1">
      Supports placeholders: <code className="font-mono">{PLACEHOLDERS}</code>. Unknown placeholders are left literal.
    </p>
  );
}

const ACCOUNT_TYPES: AccountType[] = ['brand', 'group', 'location', 'head_office', 'region', 'dealership'];
const INDUSTRIES: Industry[] = ['fitness_franchise', 'automotive_franchise', 'small_business_ecommerce', 'other'];
const STATUSES: AccountStatus[] = ['active', 'paused', 'cancelled'];
const CURRENCIES: Currency[] = ['AUD', 'NZD', 'USD', 'GBP', 'SGD'];
// Authoritative operating country (reporting §3a). A canonical list (matches the
// currency→country seed vocabulary so explicit picks and seeded values agree in
// the reporting dashboard's GROUP BY). Extend if the roster gains a new country.
const COUNTRIES = ['Australia', 'New Zealand', 'United States', 'United Kingdom', 'Singapore'];
const SERVICES: readonly Service[] = SERVICE_VALUES;
const PAID_BY: MediaSpendPaidBy[] = ['client', 'kyck'];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type FranchiseProfileFormSlice = {
  clubNamePrefix: string;
  headlines: string[];
  defaultGooglePath1: string;
  landingOffer: string;
  landingSale: string;
  landingLocation: string; // '' means null
  defaultAdCopyDocUrl: string;
  brandAssetsUrl: string;
  brandPortalNotes: string;
  defaultGymInfo: string;
  // ⚠️ The brand voice profile (2026-08-26). '' means null. Read by EVERY AI
  // copy surface — post captions AND campaign copy — so it is authored once here
  // rather than per feature. See src/lib/brandVoiceProfile.ts.
  brandVoiceProfile: string;
  // ⚠️ Provenance for an AI-drafted profile. '' means null — the AM wrote it
  // themselves, or it predates the drafter.
  brandVoiceProfileDraftedAt: string;
  // ⚠️ Anti-examples: copy we've rejected, and why. '' means null.
  brandCopyAntiExamples: string;
  defaultMetaCampaignNameTemplate: string; // '' means null
  // Pre-P2b-3 polish round 5 §2.2: brand-default Google ad-label
  // template. Same '' means null convention.
  defaultGoogleAdLabelTemplate: string;
  // OrganicAssets spec §1.1: parent Drive folder ID. '' means null. No
  // visible input yet (Phase G adds the Brand Drive settings UI); the
  // form round-trips the existing value so a generic profile save never
  // wipes a value set via the drive-parent endpoint.
  driveRolloutParentFolderId: string;
};

type FranchiseLocationFormSlice = {
  franchiseSuburb: string;
  franchiseSuburbShort: string;
  franchiseSuburbSlug: string;
  franchiseSuburbSlugTouched: boolean;
  state: string;
  gymInfo: string;
  package: string;
  defaultFormType: FormType;
  // Pre-P2b-3 polish round 5 §1: location-level Meta ad type default.
  // 'no-default' = null on the wire; the form stores the slug here for
  // ergonomics with the Select control (UI lands in the next commit).
  defaultMetaAdType: 'no-default' | 'traffic' | 'lead_gen';
  offerLandingPage: string;
  saleLandingPage: string;
  locationLandingPage: string;
  clubOpenedAt: string; // YYYY-MM-DD or ''
};

// Phase 2b — service config form slices. Money + quota values are held as STRING
// inputs ('' = null/unset) and converted at the edge (toInput): quotas via
// parseInt, spends via inputValueToCents (money.ts). Escalation contacts are
// contactId refs (null = unset), stored as ids.
type ContentCreationFormSlice = {
  channels: ServiceChannelToggles;
  quotas: {
    facebookFeed: string;
    instagramFeed: string;
    stories: string;
    googleBusiness: string;
  };
  facebookSpend: string; // dollars input
  instagramSpend: string; // dollars input
  salesContactDetailsForPost: string;
  serviceContactDetailsForPost: string;
  contentSharingGroup: string;
  notes: string;
};

type CommunityManagementFormSlice = {
  channels: ServiceChannelToggles;
  signOffPerson: string;
  escalationContacts: EscalationContacts;
};

function emptyChannelToggles(): ServiceChannelToggles {
  return { facebook: false, instagram: false, google: false, tiktok: false };
}

function emptyContentCreationSlice(): ContentCreationFormSlice {
  return {
    channels: emptyChannelToggles(),
    quotas: { facebookFeed: '', instagramFeed: '', stories: '', googleBusiness: '' },
    facebookSpend: '',
    instagramSpend: '',
    salesContactDetailsForPost: '',
    serviceContactDetailsForPost: '',
    contentSharingGroup: '',
    notes: '',
  };
}

function emptyCommunityManagementSlice(): CommunityManagementFormSlice {
  return {
    channels: emptyChannelToggles(),
    signOffPerson: '',
    escalationContacts: { primary: null, sales: null, service: null, backup: null },
  };
}

const intToInput = (n: number | null | undefined): string =>
  typeof n === 'number' ? String(n) : '';

function ccSliceFrom(c: ContentCreationConfig | undefined): ContentCreationFormSlice {
  if (!c) return emptyContentCreationSlice();
  // Belt-and-braces on the 2026-08-05 split: the server already normalises on
  // read, but a form that blanked the sales block off a not-yet-normalised doc
  // would SAVE the blank.
  const cc = normalizeContentCreationConfig(c);
  return {
    channels: { ...emptyChannelToggles(), ...c.channels },
    quotas: {
      facebookFeed: intToInput(c.quotas?.facebookFeed),
      instagramFeed: intToInput(c.quotas?.instagramFeed),
      stories: intToInput(c.quotas?.stories),
      googleBusiness: intToInput(c.quotas?.googleBusiness),
    },
    facebookSpend: centsToInputValue(c.mediaBudget?.facebookSpendCents ?? null),
    instagramSpend: centsToInputValue(c.mediaBudget?.instagramSpendCents ?? null),
    salesContactDetailsForPost: cc.salesContactDetailsForPost,
    serviceContactDetailsForPost: cc.serviceContactDetailsForPost,
    contentSharingGroup: c.contentSharingGroup ?? '',
    notes: c.notes ?? '',
  };
}

function cmSliceFrom(
  c: CommunityManagementConfig | undefined,
): CommunityManagementFormSlice {
  if (!c) return emptyCommunityManagementSlice();
  return {
    channels: { ...emptyChannelToggles(), ...c.channels },
    signOffPerson: c.signOffPerson ?? '',
    escalationContacts: {
      primary: c.escalationContacts?.primary ?? null,
      sales: c.escalationContacts?.sales ?? null,
      service: c.escalationContacts?.service ?? null,
      backup: c.escalationContacts?.backup ?? null,
    },
  };
}

// Dealership ads config slice (meta_aia / google_vla): budget dollars string +
// feed URL string. Budget is cents at the edges (centsToInputValue on load,
// inputValueToCents on save).
type DealershipAdsFormSlice = { budget: string; feedUrl: string };
function emptyDealershipAdsSlice(): DealershipAdsFormSlice {
  return { budget: '', feedUrl: '' };
}
function dealershipAdsSliceFrom(c: DealershipAdsConfig | undefined): DealershipAdsFormSlice {
  if (!c) return emptyDealershipAdsSlice();
  return { budget: centsToInputValue(c.budgetCents ?? null), feedUrl: c.feedUrl ?? '' };
}

type FormState = {
  name: string;
  slug: string;
  slugTouched: boolean;
  parentAccountId: string | null;
  accountType: AccountType;
  industry: Industry;
  status: AccountStatus;
  accountManagerId: string;
  mediaSpendPaidBy: MediaSpendPaidBy;
  salesforceAccountId: string;
  phone: string;
  // Always an Address object in the form (the tickbox is gone — always editable);
  // toInput emits null when every field is blank so we don't store empty addresses.
  address: Address;
  metaBudget: string;
  googleBudget: string;
  currency: Currency | '';
  country: string; // authoritative operating country (reporting §3a)
  services: Set<Service>;
  // Phase 2a — runsMeta/runsGoogle are no longer edited directly: they are
  // DERIVED from the services set at save time (toInput). The `meta_ads` /
  // `google_ads` service checkboxes are the control, and ticking one reveals its
  // budget field. No standalone form state for the flags.
  // Phase 2b — per-service config, held whole (both slices always present with
  // defaults) so an unticked service's config is RETAINED across a save. Emitted
  // by toInput only when a configurable service is active or the account already
  // had a serviceConfig (see hadServiceConfig).
  contentCreation: ContentCreationFormSlice;
  communityManagement: CommunityManagementFormSlice;
  // Dealership ads config (revealed when meta_aia / google_vla ticked).
  metaAia: DealershipAdsFormSlice;
  googleVla: DealershipAdsFormSlice;
  hadServiceConfig: boolean;
  // Build A — opt-in to the recurring Meta reporting pull (Cloud Run Job).
  reportingSyncEnabled: boolean;
  // Goal-split Meta client report — meaningful on BRAND accounts only.
  portalMetaGoalReport: boolean;
  // Client report template — '' = inherit (NOT the same as 'standard').
  reportTemplate: ReportTemplate | '';
  fbAdAccountId: string;
  // §3b Shared Ad Accounts — the checkbox + its per-location campaign-name match
  // tokens. Tokens stored as authored (case + variants preserved, e.g. "Rosebay"
  // + "Rose Bay"); normalised at dimension sync.
  isSharedAdAccount: boolean;
  adAccountMatchTokens: string[];
  // Stage 7A. Carried silently (no UI field yet — set via the Phase-C settings
  // UI). Preserved through edit so saving the form never wipes an existing link.
  zernioProfileId: string | null;
  // Stage 7A D2.11 — account-level Instagram default (id + display name).
  defaultInstagramAccountId: string;
  defaultInstagramAccountName: string;
  // Feature A — account-level Meta Ad Builder defaults (pixel + lead form). '' = none.
  // The *Name mirrors the id (denormalised label for the Accounts grid), captured
  // from the live picker when the id is chosen.
  defaultPixelId: string;
  defaultPixelName: string;
  defaultLeadFormId: string;
  defaultLeadFormName: string;
  fbAdAccountUrl: string;
  googleAdsCustomerId: string;
  crmId: string;
  clubwiseCode: string;
  googleAdsUrl: string;
  googleAnalyticsPropertyId: string;
  facebookPageUrl: string;
  instagramUrl: string;
  tiktokUrl: string;
  googleBusinessProfileUrl: string;
  websiteUrl: string;
  reportUrl: string;
  clientDriveUrl: string;
  internalDriveUrl: string;
  // Tracking / integration references (2026-07-24).
  gtmContainerId: string;
  metaVerifiedAdvertiserId: string;
  trackingDocUrl: string;
  zapierFolderUrl: string;
  // Dealership fields (2026-07-24). brandAccountId '' = none.
  brandAccountId: string;
  dealershipWebsitePlatform: string;
  dealershipCrmSystem: string;
  description: string;
  targetingNotes: string;
  notes: string;
  leadSyncingNotes: string;
  // Attached-profile state. `attach*` is true when the form should save the
  // profile alongside the account. In edit mode it's pinned true when the
  // profile already exists (we always save edits through); in create mode
  // it's a user-toggled checkbox ("also attach a franchise profile now").
  attachFp: boolean;
  fp: FranchiseProfileFormSlice;
  attachLp: boolean;
  lp: FranchiseLocationFormSlice;
};

function emptyAddress(): Address {
  return { street: '', suburb: '', state: '', postcode: '', country: '' };
}

// Every field blank → store null (no empty-address docs) once trimmed.
function addressIsBlank(a: Address): boolean {
  return (
    a.street.trim() === '' &&
    a.suburb.trim() === '' &&
    a.state.trim() === '' &&
    a.postcode.trim() === '' &&
    a.country.trim() === ''
  );
}

function emptyFpSlice(): FranchiseProfileFormSlice {
  return {
    clubNamePrefix: '',
    headlines: [],
    defaultGooglePath1: '',
    landingOffer: '',
    landingSale: '',
    landingLocation: '',
    defaultAdCopyDocUrl: '',
    brandAssetsUrl: '',
    brandPortalNotes: '',
    defaultGymInfo: '',
    brandVoiceProfile: '',
    brandVoiceProfileDraftedAt: '',
    brandCopyAntiExamples: '',
    defaultMetaCampaignNameTemplate: '',
    defaultGoogleAdLabelTemplate: '',
    driveRolloutParentFolderId: '',
  };
}

function fpSliceFrom(p: FranchiseProfile): FranchiseProfileFormSlice {
  return {
    clubNamePrefix: p.clubNamePrefix,
    headlines: [...p.defaultGoogleBoilerplateHeadlines],
    defaultGooglePath1: p.defaultGooglePath1,
    landingOffer: p.landingPageTemplate.offer,
    landingSale: p.landingPageTemplate.sale,
    landingLocation: p.landingPageTemplate.location ?? '',
    defaultAdCopyDocUrl: p.defaultAdCopyDocUrl ?? '',
    brandAssetsUrl: p.brandAssetsUrl ?? '',
    brandPortalNotes: p.brandPortalNotes ?? '',
    defaultGymInfo: p.defaultGymInfo ?? '',
    // Legacy profiles predate the voice profile — undefined → ''.
    brandVoiceProfile: p.brandVoiceProfile ?? '',
    brandVoiceProfileDraftedAt: p.brandVoiceProfileDraftedAt ?? '',
    brandCopyAntiExamples: p.brandCopyAntiExamples ?? '',
    // Legacy FranchiseProfile docs may not carry this field yet; treat
    // undefined as empty so the form mounts cleanly.
    defaultMetaCampaignNameTemplate: p.defaultMetaCampaignNameTemplate ?? '',
    // Pre-P2b-3 polish round 5 §2.2: same legacy-undefined tolerance
    // as the Meta default above.
    defaultGoogleAdLabelTemplate: p.defaultGoogleAdLabelTemplate ?? '',
    // OrganicAssets spec §1.1: legacy brand profiles predate the field;
    // coalesce undefined → ''. Round-tripped unchanged by fpToInput.
    driveRolloutParentFolderId: p.driveRolloutParentFolderId ?? '',
  };
}

function emptyLpSlice(): FranchiseLocationFormSlice {
  return {
    franchiseSuburb: '',
    franchiseSuburbShort: '',
    franchiseSuburbSlug: '',
    franchiseSuburbSlugTouched: false,
    state: '',
    gymInfo: '',
    package: '',
    defaultFormType: 'Generic',
    defaultMetaAdType: 'no-default',
    offerLandingPage: '',
    saleLandingPage: '',
    locationLandingPage: '',
    clubOpenedAt: '',
  };
}

function lpSliceFrom(p: FranchiseLocationProfile): FranchiseLocationFormSlice {
  return {
    franchiseSuburb: p.franchiseSuburb,
    franchiseSuburbShort: p.franchiseSuburbShort ?? '',
    franchiseSuburbSlug: p.franchiseSuburbSlug,
    franchiseSuburbSlugTouched: true,
    state: p.state,
    gymInfo: p.gymInfo ?? '',
    package: p.package ?? '',
    defaultFormType: p.defaultFormType,
    defaultMetaAdType: p.defaultMetaAdType ?? 'no-default',
    // Form slice stays string for ergonomics with text inputs. null
    // and empty string both map to '' in the editor; lpToInput maps
    // back to null on save so storage prefers the null shape.
    offerLandingPage: p.offerLandingPage ?? '',
    saleLandingPage: p.saleLandingPage ?? '',
    locationLandingPage: p.locationLandingPage ?? '',
    clubOpenedAt: p.clubOpenedAt ? p.clubOpenedAt.slice(0, 10) : '',
  };
}

// Exported for unit tests (form state ⇆ AccountInput mapping).
export function initialState(mode: Mode): FormState {
  if (mode.kind === 'create') {
    return {
      name: '',
      slug: '',
      slugTouched: false,
      parentAccountId: null,
      accountType: 'brand',
      industry: 'fitness_franchise',
      status: 'active',
      accountManagerId: '',
      mediaSpendPaidBy: 'client',
      salesforceAccountId: '',
      phone: '',
      address: emptyAddress(),
      metaBudget: '',
      googleBudget: '',
      currency: 'AUD',
      country: '',
      services: new Set(),
      contentCreation: emptyContentCreationSlice(),
      communityManagement: emptyCommunityManagementSlice(),
      metaAia: emptyDealershipAdsSlice(),
      googleVla: emptyDealershipAdsSlice(),
      hadServiceConfig: false,
      reportingSyncEnabled: false,
      portalMetaGoalReport: false,
      reportTemplate: '',
      fbAdAccountId: '',
      isSharedAdAccount: false,
      adAccountMatchTokens: [],
      zernioProfileId: null,
      defaultInstagramAccountId: '',
      defaultInstagramAccountName: '',
      defaultPixelId: '',
      defaultPixelName: '',
      defaultLeadFormId: '',
      defaultLeadFormName: '',
      fbAdAccountUrl: '',
      googleAdsCustomerId: '',
      crmId: '',
      clubwiseCode: '',
      googleAdsUrl: '',
      googleAnalyticsPropertyId: '',
      facebookPageUrl: '',
      instagramUrl: '',
      tiktokUrl: '',
      googleBusinessProfileUrl: '',
      websiteUrl: '',
      reportUrl: '',
      clientDriveUrl: '',
      internalDriveUrl: '',
      gtmContainerId: '',
      metaVerifiedAdvertiserId: '',
      trackingDocUrl: '',
      zapierFolderUrl: '',
      brandAccountId: '',
      dealershipWebsitePlatform: '',
      dealershipCrmSystem: '',
      description: '',
      targetingNotes: '',
      notes: '',
      leadSyncingNotes: '',
      attachFp: false,
      fp: emptyFpSlice(),
      attachLp: false,
      lp: emptyLpSlice(),
    };
  }
  const a = mode.account;
  return {
    name: a.name,
    slug: a.slug,
    slugTouched: true,
    parentAccountId: a.parentAccountId,
    accountType: a.accountType,
    industry: a.industry,
    status: a.status,
    accountManagerId: a.accountManagerId ?? '',
    mediaSpendPaidBy: a.mediaSpendPaidBy,
    salesforceAccountId: a.salesforceAccountId ?? '',
    phone: a.phone ?? '',
    address: a.address ?? emptyAddress(),
    metaBudget: centsToInputValue(a.metaMasterMonthlyBudget),
    googleBudget: centsToInputValue(a.googleMasterMonthlyBudget),
    currency: (a.currency ?? '') as Currency | '',
    country: a.country ?? '',
    services: new Set(a.services),
    contentCreation: ccSliceFrom(a.serviceConfig?.contentCreation),
    communityManagement: cmSliceFrom(a.serviceConfig?.communityManagement),
    metaAia: dealershipAdsSliceFrom(a.serviceConfig?.metaAia),
    googleVla: dealershipAdsSliceFrom(a.serviceConfig?.googleVla),
    hadServiceConfig: a.serviceConfig != null,
    // Build A opt-in; legacy/unset docs default false (not synced until enabled).
    reportingSyncEnabled: a.reportingSyncEnabled ?? false,
    portalMetaGoalReport: a.portalMetaGoalReport ?? false,
    reportTemplate: a.reportTemplate ?? '',
    fbAdAccountId: a.fbAdAccountId ?? '',
    isSharedAdAccount: a.isSharedAdAccount ?? false,
    adAccountMatchTokens: a.adAccountMatchTokens ?? [],
    zernioProfileId: a.zernioProfileId ?? null,
    defaultInstagramAccountId: a.defaultInstagramAccountId ?? '',
    defaultInstagramAccountName: a.defaultInstagramAccountName ?? '',
    defaultPixelId: a.defaultPixelId ?? '',
    defaultPixelName: a.defaultPixelName ?? '',
    defaultLeadFormId: a.defaultLeadFormId ?? '',
    defaultLeadFormName: a.defaultLeadFormName ?? '',
    fbAdAccountUrl: a.fbAdAccountUrl ?? '',
    googleAdsCustomerId: a.googleAdsCustomerId ?? '',
    crmId: a.crmId ?? '',
    clubwiseCode: a.clubwiseCode ?? '',
    googleAdsUrl: a.googleAdsUrl ?? '',
    googleAnalyticsPropertyId: a.googleAnalyticsPropertyId ?? '',
    facebookPageUrl: a.facebookPageUrl ?? '',
    instagramUrl: a.instagramUrl ?? '',
    tiktokUrl: a.tiktokUrl ?? '',
    googleBusinessProfileUrl: a.googleBusinessProfileUrl ?? '',
    websiteUrl: a.websiteUrl ?? '',
    reportUrl: a.reportUrl ?? '',
    clientDriveUrl: a.clientDriveUrl ?? '',
    internalDriveUrl: a.internalDriveUrl ?? '',
    gtmContainerId: a.gtmContainerId ?? '',
    metaVerifiedAdvertiserId: a.metaVerifiedAdvertiserId ?? '',
    trackingDocUrl: a.trackingDocUrl ?? '',
    zapierFolderUrl: a.zapierFolderUrl ?? '',
    brandAccountId: a.brandAccountId ?? '',
    dealershipWebsitePlatform: a.dealershipWebsitePlatform ?? '',
    dealershipCrmSystem: a.dealershipCrmSystem ?? '',
    description: a.description ?? '',
    targetingNotes: a.targetingNotes ?? '',
    notes: a.notes ?? '',
    leadSyncingNotes: a.leadSyncingNotes ?? '',
    attachFp: mode.franchiseProfile !== null,
    fp: mode.franchiseProfile ? fpSliceFrom(mode.franchiseProfile) : emptyFpSlice(),
    attachLp: mode.franchiseLocationProfile !== null,
    lp: mode.franchiseLocationProfile
      ? lpSliceFrom(mode.franchiseLocationProfile)
      : emptyLpSlice(),
  };
}

// Parse a quota string input → non-negative integer or null (''/invalid = null).
function quotaOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// Build the stored ServiceConfig from the form slices. Emitted only when a
// configurable service is active OR the account already carried a config (so a
// fresh account without content/community services stays serviceConfig-less,
// while retain-on-untick keeps an existing config intact).
export function buildServiceConfig(state: FormState): ServiceConfig | undefined {
  const wants =
    state.services.has('content_creation') ||
    state.services.has('community_management') ||
    state.services.has('meta_aia') ||
    state.services.has('google_vla') ||
    state.hadServiceConfig;
  if (!wants) return undefined;
  const cc = state.contentCreation;
  const cm = state.communityManagement;
  const config: ServiceConfig = {
    contentCreation: {
      channels: cc.channels,
      quotas: {
        facebookFeed: quotaOrNull(cc.quotas.facebookFeed),
        instagramFeed: quotaOrNull(cc.quotas.instagramFeed),
        stories: quotaOrNull(cc.quotas.stories),
        googleBusiness: quotaOrNull(cc.quotas.googleBusiness),
      },
      mediaBudget: {
        facebookSpendCents: inputValueToCents(cc.facebookSpend),
        instagramSpendCents: inputValueToCents(cc.instagramSpend),
      },
      salesContactDetailsForPost: cc.salesContactDetailsForPost,
      serviceContactDetailsForPost: cc.serviceContactDetailsForPost,
      contentSharingGroup: cc.contentSharingGroup.trim(),
      notes: cc.notes,
    },
    communityManagement: {
      channels: cm.channels,
      signOffPerson: cm.signOffPerson.trim(),
      escalationContacts: cm.escalationContacts,
    },
  };
  // Dealership ads blocks — emitted only while the service is ticked (config
  // revealed when ticked; not retained on untick, unlike content/community).
  if (state.services.has('meta_aia')) {
    config.metaAia = {
      budgetCents: inputValueToCents(state.metaAia.budget),
      feedUrl: state.metaAia.feedUrl.trim() || null,
    };
  }
  if (state.services.has('google_vla')) {
    config.googleVla = {
      budgetCents: inputValueToCents(state.googleVla.budget),
      feedUrl: state.googleVla.feedUrl.trim() || null,
    };
  }
  return config;
}

export function toInput(state: FormState): AccountInput {
  const trimOrNull = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    name: state.name.trim(),
    slug: state.slug.trim(),
    parentAccountId: state.parentAccountId,
    accountType: state.accountType,
    industry: state.industry,
    status: state.status,
    accountManagerId: trimOrNull(state.accountManagerId),
    mediaSpendPaidBy: state.mediaSpendPaidBy,
    salesforceAccountId: trimOrNull(state.salesforceAccountId),
    phone: trimOrNull(state.phone),
    country: trimOrNull(state.country),
    address: addressIsBlank(state.address) ? null : state.address,
    metaMasterMonthlyBudget: inputValueToCents(state.metaBudget),
    googleMasterMonthlyBudget: inputValueToCents(state.googleBudget),
    // Every Account now carries its own currency (spec updated; see
    // scripts/migrate-currency-to-all-accounts.ts). The form always holds a
    // concrete value -- create defaults to AUD, edit loads the stored value,
    // and selecting a parent in create mode auto-fills from the parent.
    currency: (state.currency || 'AUD') as Currency,
    services: [...state.services],
    // Phase 2a — runsMeta/runsGoogle derive from services (client mirror of the
    // server's authoritative derive-on-save). meta_ads → runsMeta, google_ads →
    // runsGoogle.
    ...deriveRunFlagsFromServices([...state.services]),
    // Phase 2b — structured per-service config (undefined when no configurable
    // service is/was active, so it isn't stored on accounts that don't use it).
    serviceConfig: buildServiceConfig(state),
    reportingSyncEnabled: state.reportingSyncEnabled,
    // ⚠️ Sent only for a brand — the flag is read off the BRAND account, so it
    // means nothing on any other type and would only add noise to their audit.
    ...(state.accountType === 'brand' ? { portalMetaGoalReport: state.portalMetaGoalReport } : {}),
    // null = "use inherited" (clears an override); the audit trail records the change.
    reportTemplate: state.reportTemplate === '' ? null : state.reportTemplate,
    fbAdAccountId: trimOrNull(state.fbAdAccountId),
    // §3b — stored as authored (case + spelling variants preserved); the
    // dimension sync lowercases/trims/dedupes for matching. Drop blank chips.
    isSharedAdAccount: state.isSharedAdAccount,
    adAccountMatchTokens: state.adAccountMatchTokens.map((t) => t.trim()).filter(Boolean),
    zernioProfileId: state.zernioProfileId, // carried through; preserved on edit
    defaultInstagramAccountId: trimOrNull(state.defaultInstagramAccountId),
    defaultInstagramAccountName: trimOrNull(state.defaultInstagramAccountName),
    defaultPixelId: trimOrNull(state.defaultPixelId),
    defaultPixelName: trimOrNull(state.defaultPixelName),
    defaultLeadFormId: trimOrNull(state.defaultLeadFormId),
    defaultLeadFormName: trimOrNull(state.defaultLeadFormName),
    fbAdAccountUrl: trimOrNull(state.fbAdAccountUrl),
    googleAdsCustomerId: trimOrNull(state.googleAdsCustomerId),
    crmId: trimOrNull(state.crmId),
    clubwiseCode: trimOrNull(state.clubwiseCode),
    googleAdsUrl: trimOrNull(state.googleAdsUrl),
    googleAnalyticsPropertyId: trimOrNull(state.googleAnalyticsPropertyId),
    facebookPageUrl: trimOrNull(state.facebookPageUrl),
    instagramUrl: trimOrNull(state.instagramUrl),
    tiktokUrl: trimOrNull(state.tiktokUrl),
    googleBusinessProfileUrl: trimOrNull(state.googleBusinessProfileUrl),
    websiteUrl: trimOrNull(state.websiteUrl),
    reportUrl: trimOrNull(state.reportUrl),
    clientDriveUrl: trimOrNull(state.clientDriveUrl),
    internalDriveUrl: trimOrNull(state.internalDriveUrl),
    gtmContainerId: trimOrNull(state.gtmContainerId),
    metaVerifiedAdvertiserId: trimOrNull(state.metaVerifiedAdvertiserId),
    trackingDocUrl: trimOrNull(state.trackingDocUrl),
    zapierFolderUrl: trimOrNull(state.zapierFolderUrl),
    brandAccountId: trimOrNull(state.brandAccountId),
    dealershipWebsitePlatform: trimOrNull(state.dealershipWebsitePlatform),
    dealershipCrmSystem: trimOrNull(state.dealershipCrmSystem),
    description: trimOrNull(state.description),
    targetingNotes: trimOrNull(state.targetingNotes),
    notes: trimOrNull(state.notes),
    leadSyncingNotes: trimOrNull(state.leadSyncingNotes),
  };
}

function fpToInput(accountId: string, slice: FranchiseProfileFormSlice): FranchiseProfileInput {
  const trimOrNull = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    accountId,
    clubNamePrefix: slice.clubNamePrefix.trim(),
    defaultGoogleBoilerplateHeadlines: slice.headlines
      .map((h) => h.trim())
      .filter((h) => h.length > 0),
    defaultGooglePath1: slice.defaultGooglePath1.trim(),
    landingPageTemplate: {
      offer: slice.landingOffer.trim(),
      sale: slice.landingSale.trim(),
      location: slice.landingLocation.trim() === '' ? null : slice.landingLocation.trim(),
    },
    defaultAdCopyDocUrl: trimOrNull(slice.defaultAdCopyDocUrl),
    brandAssetsUrl: trimOrNull(slice.brandAssetsUrl),
    brandPortalNotes: trimOrNull(slice.brandPortalNotes),
    defaultGymInfo: trimOrNull(slice.defaultGymInfo),
    brandVoiceProfile: trimOrNull(slice.brandVoiceProfile),
    brandVoiceProfileDraftedAt: trimOrNull(slice.brandVoiceProfileDraftedAt),
    brandCopyAntiExamples: trimOrNull(slice.brandCopyAntiExamples),
    defaultMetaCampaignNameTemplate: trimOrNull(slice.defaultMetaCampaignNameTemplate),
    defaultGoogleAdLabelTemplate: trimOrNull(slice.defaultGoogleAdLabelTemplate),
    driveRolloutParentFolderId: trimOrNull(slice.driveRolloutParentFolderId),
  };
}

function lpToInput(accountId: string, slice: FranchiseLocationFormSlice): FranchiseLocationProfileInput {
  const trimOrNull = (s: string) => (s.trim() === '' ? null : s.trim());
  // HTML date input returns YYYY-MM-DD; server expects ISO datetime. Append
  // T00:00:00.000Z so new Date() parses consistently and zod's .datetime()
  // accepts it.
  const clubOpenedAt = slice.clubOpenedAt
    ? new Date(`${slice.clubOpenedAt}T00:00:00.000Z`).toISOString()
    : null;
  return {
    accountId,
    franchiseSuburb: slice.franchiseSuburb.trim(),
    franchiseSuburbShort: trimOrNull(slice.franchiseSuburbShort),
    franchiseSuburbSlug: slice.franchiseSuburbSlug.trim(),
    state: slice.state.trim(),
    gymInfo: trimOrNull(slice.gymInfo),
    package: trimOrNull(slice.package),
    defaultFormType: slice.defaultFormType,
    defaultMetaAdType:
      slice.defaultMetaAdType === 'no-default' ? null : slice.defaultMetaAdType,
    // Per-location landing URL overrides: empty string maps to null
    // so the resolver inherits from the brand's landingPageTemplate.
    // Storage prefers null over empty string. Round-5 (null=inherit)
    // landed this for offer/sale; locationLandingPage already had it.
    offerLandingPage: trimOrNull(slice.offerLandingPage),
    saleLandingPage: trimOrNull(slice.saleLandingPage),
    locationLandingPage: trimOrNull(slice.locationLandingPage),
    clubOpenedAt,
  };
}

const inputClass =
  'w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

function Text({ label, value, onChange, required, placeholder, disabled, hint, className = '' }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  // Disable the underlying input while keeping the value visible. Used by
  // platform-gated budget fields (Pre-P2b-3 §2 polish): when an Account has
  // runsMeta=false, the metaMasterMonthlyBudget input is disabled so the
  // existing value persists but can't drift further.
  disabled?: boolean;
  // Optional small italic suffix shown beside the label — used to annotate
  // why a field is disabled. Not a tooltip; AMs see the explanation inline.
  hint?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className={labelClass}>
        {label}
        {required && <span className="text-red-500"> *</span>}
        {hint && <span className="ml-2 text-xs italic text-slate-400 font-normal">{hint}</span>}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; rows?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <Textarea
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Select<T extends string>({ label, value, options, onChange, required }: {
  label: string; value: T; options: readonly T[]; onChange: (v: T) => void; required?: boolean;
}) {
  return (
    <div>
      <label className={labelClass}>{label}{required && <span className="text-red-500"> *</span>}</label>
      <SelectField
        aria-label={label}
        value={value}
        onValueChange={(v) => onChange(v as T)}
        options={options.map((o) => ({ value: o, label: o }))}
      />
    </div>
  );
}

// Feature A — an account-level default selector (pixel / lead form) sourced from
// the account's LIVE Zernio resources. Renders a real picker only when the
// account is connected (edit mode + a linked Zernio profile + an ad account);
// otherwise falls back to a plain id text input so the value is still editable
// (and never lost). A stored id that isn't in the current live list is preserved
// as its own option so refreshing/editing never silently drops it.
function DefaultResourceSelect({
  label,
  hint,
  value,
  onChange,
  enabled,
  accountId,
  resource,
}: {
  label: string;
  hint: string;
  value: string;
  // Emits BOTH the chosen id and its display name (resolved from the live list)
  // so the caller can persist the denormalised *Name. name is null when unknown
  // (manual id entry while disconnected, or an id no longer in the list).
  onChange: (id: string, name: string | null) => void;
  enabled: boolean;
  accountId: string;
  resource: 'pixels' | 'lead-forms';
}) {
  const query = useQuery({
    queryKey: ['accountDefaultResource', resource, accountId],
    queryFn: () =>
      resource === 'pixels'
        ? getPixels(accountId).then((r) => r.pixels.map((p) => ({ id: p.id, name: p.name })))
        : getLeadForms(accountId).then((r) => r.leadForms.map((f) => ({ id: f.id, name: f.name }))),
    enabled,
  });

  const options = query.data ?? [];
  const knownIds = new Set(options.map((o) => o.id));

  if (!enabled) {
    // Manual id entry (account not Meta-connected): no live list, so the name is
    // unknown — emit null. The backfill (or a later connected edit) fills it.
    return (
      <Text
        label={label}
        value={value}
        onChange={(v) => onChange(v, null)}
        placeholder="Connect Meta to pick from the live list"
        hint={hint}
      />
    );
  }

  return (
    <div>
      <label className={labelClass}>
        {label}
        <span className="ml-2 text-xs italic text-slate-400 font-normal">{hint}</span>
      </label>
      <Combobox
        aria-label={label}
        searchPlaceholder="Search…"
        value={value}
        disabled={query.isLoading}
        onValueChange={(v) => onChange(v, options.find((o) => o.id === v)?.name ?? null)}
        options={[
          { value: '', label: '— None —' },
          ...(value !== '' && !knownIds.has(value)
            ? [{ value, label: `${value} (not in current list)` }]
            : []),
          ...options.map((o) => ({ value: o.id, label: o.name, keywords: o.id })),
        ]}
      />
      {query.isLoading && <p className="mt-1 text-xs text-slate-400">Loading…</p>}
      {query.isError && (
        <p className="mt-1 text-xs text-amber-600">Couldn’t load from Meta — enter the id manually if needed.</p>
      )}
    </div>
  );
}

// Phase 2b — the 4-channel toggle row shared by both service configs.
const SERVICE_CHANNELS: Array<{ key: keyof ServiceChannelToggles; label: string }> = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'google', label: 'Google' },
  { key: 'tiktok', label: 'TikTok' },
];

function ChannelTogglesField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ServiceChannelToggles;
  onChange: (v: ServiceChannelToggles) => void;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap gap-4">
        {SERVICE_CHANNELS.map((c) => (
          <label key={c.key} className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value[c.key]}
              onChange={(e) => onChange({ ...value, [c.key]: e.target.checked })}
            />
            {c.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// Phase 2b — escalation-contact picker. Links an existing contact of THIS
// account (contactId ref) or opens the new-contact page (same account preset) in
// a new tab; returning refetches so the fresh contact appears. A stored id that
// isn't in the account's linked list is preserved as its own option so editing
// never silently drops it. Disabled in create mode (no account id yet).
function EscalationContactPicker({
  accountId,
  label,
  value,
  onChange,
}: {
  accountId: string;
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const contactsQuery = useQuery({
    queryKey: ['contacts', accountId],
    queryFn: () => listContacts(accountId),
    enabled: accountId !== '',
  });
  const contacts = contactsQuery.data ?? [];
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Combobox
            aria-label={label}
            searchPlaceholder="Search contacts…"
            value={value ?? ''}
            disabled={accountId === ''}
            onValueChange={(v) => onChange(v === '' ? null : v)}
            options={escalationContactOptions(accountId, contacts, value)}
          />
        </div>
        {accountId !== '' && (
          <a
            href={`/contacts/new?account=${encodeURIComponent(accountId)}`}
            target="_blank"
            rel="noreferrer"
            className="whitespace-nowrap text-sm text-blue-600 hover:underline"
          >
            + New
          </a>
        )}
      </div>
      {accountId === '' && (
        <p className="mt-1 text-xs text-slate-500">Save the account first to link contacts.</p>
      )}
    </div>
  );
}

function SectionBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

// Round-5 (null=inherit): rendered below each per-location landing
// URL input. Surfaces what the brand template would resolve to for
// the current form state so the AM can:
//   - see what the inherited value will be when the input is empty;
//   - compare their override against the inherited default when the
//     input has a value;
//   - notice when the brand template has missing placeholders (e.g.
//     suburb slug not yet authored on the form) before saving.
//
// Template substitution runs against form state (not the saved row)
// so the preview updates live as the AM types into Suburb / Slug /
// State.
function InheritedFromBrand({
  template,
  account,
  locationStandIn,
  inputValue,
  onRevert,
}: {
  template: string | null | undefined;
  account: { name: string; slug: string };
  locationStandIn: FranchiseLocationProfile;
  inputValue: string;
  // Round-5 commit 4 polish: when the input has a non-empty value
  // (override active), surface a "Revert to inherit" link that
  // clears the field. Saves the AM selecting + deleting text and
  // makes the inherit-by-default behaviour discoverable.
  onRevert?: () => void;
}) {
  const overrideActive = inputValue.trim().length > 0;
  if (!template || template.length === 0) {
    return (
      <p className="mt-1 text-xs text-slate-500 italic">
        No brand template authored — set URL manually if needed.
        {overrideActive && onRevert && (
          <>
            {' '}
            <RevertToInheritButton onClick={onRevert} />
          </>
        )}
      </p>
    );
  }
  const { output, missingPlaceholders } = renderTemplate(
    template,
    account,
    locationStandIn,
  );
  if (missingPlaceholders.length > 0) {
    return (
      <p className="mt-1 text-xs text-amber-600">
        Inherited URL incomplete — missing:{' '}
        <code className="font-mono">{missingPlaceholders.join(', ')}</code>. Set URL
        manually or finish authoring location slug / state.
        {overrideActive && onRevert && (
          <>
            {' '}
            <RevertToInheritButton onClick={onRevert} />
          </>
        )}
      </p>
    );
  }
  return (
    <p className="mt-1 text-xs text-slate-500">
      {overrideActive ? 'Override active. Inherited would be: ' : 'Currently inheriting → '}
      <code className="font-mono break-all text-slate-700">{output}</code>
      {overrideActive && onRevert && (
        <>
          {' · '}
          <RevertToInheritButton onClick={onRevert} />
        </>
      )}
    </p>
  );
}

// Inline link-styled button that clears the override input. Only
// rendered when the input currently has a non-empty value (the
// override is active). Clicking sets the form slice value to '',
// which maps to null on save through lpToInput.
function RevertToInheritButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-blue-600 hover:underline focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
    >
      Revert to inherit
    </button>
  );
}

// Build a minimal FranchiseLocationProfile from form state so
// renderTemplate can substitute placeholders against the AM's live
// edits. Empty strings normalise to null per the resolver's
// inherit-on-empty rule.
function lpStandInFromForm(
  slice: FranchiseLocationFormSlice,
  accountId: string,
): FranchiseLocationProfile {
  const trimOrNullLocal = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    accountId,
    franchiseSuburb: slice.franchiseSuburb,
    franchiseSuburbShort: trimOrNullLocal(slice.franchiseSuburbShort),
    franchiseSuburbSlug: slice.franchiseSuburbSlug,
    state: slice.state,
    gymInfo: trimOrNullLocal(slice.gymInfo),
    package: trimOrNullLocal(slice.package),
    defaultFormType: slice.defaultFormType,
    defaultMetaAdType:
      slice.defaultMetaAdType === 'no-default' ? null : slice.defaultMetaAdType,
    offerLandingPage: trimOrNullLocal(slice.offerLandingPage),
    saleLandingPage: trimOrNullLocal(slice.saleLandingPage),
    locationLandingPage: trimOrNullLocal(slice.locationLandingPage),
    clubOpenedAt: null,
    // Synthetic timestamps — renderTemplate doesn't read these.
    createdAt: '',
    updatedAt: '',
  };
}

export function AccountForm({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const allAccountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [state, setState] = useState<FormState>(() => initialState(mode));
  const [error, setError] = useState<string | null>(null);
  // `includeUid` keeps the CURRENT manager in the list even if their access was turned off,
  // so they read as "Name (disabled)" rather than "(unknown user: ...)".
  const usersQuery = useQuery({
    queryKey: ['users', state.accountManagerId || null],
    queryFn: () => listUsers(undefined, state.accountManagerId || undefined),
  });

  // Feature A — the default pixel / lead-form selectors fetch LIVE Zernio
  // resources, which need an existing account with a linked profile + ad account.
  // Create mode (no id yet) and unconnected accounts fall back to manual id entry.
  const metaConnected =
    mode.kind === 'edit' && !!mode.account.zernioProfileId && !!mode.account.fbAdAccountId;

  // §3b — sharedness is GROUND TRUTH (≥2 accounts on one ad account), so surface
  // it even before the checkbox is ticked. Computed client-side from the roster
  // the form already loads (listAccounts carries both platform ids + the tokens),
  // keyed on the CURRENTLY-typed ids: every OTHER account on them, plus this one.
  //
  // PLATFORM-AGNOSTIC: a location can share a Meta ad account, a Google Ads
  // customer, or both — and match tokens are ONE list serving both, so any shared
  // cluster is a reason to set them. One entry per shared platform so the hint can
  // name which side is shared (their counts genuinely differ).
  // The same token check the grid uses (hasMatchToken — trims, ignores blanks),
  // via the SAME module, so this alert and the grid's "Ad account sharing" column
  // agree by construction.
  const sharedClusters = useMemo(
    () =>
      describeSharedClusters(
        {
          fbAdAccountId: state.fbAdAccountId,
          googleAdsCustomerId: state.googleAdsCustomerId,
          adAccountMatchTokens: state.adAccountMatchTokens,
        },
        (allAccountsQuery.data ?? []).filter(
          (a) => mode.kind !== 'edit' || a.id !== mode.account.id,
        ),
      ),
    [
      allAccountsQuery.data,
      state.fbAdAccountId,
      state.googleAdsCustomerId,
      state.adAccountMatchTokens,
      mode,
    ],
  );

  // Detected-shared on either platform. Drives the token box, so a Google-shared
  // location no longer has to tick a Meta-sounding checkbox to reach the field.
  const detectedShared = sharedClusters.length > 0;
  const sharedOnLabel = describeSharedOn(sharedClusters.map((c) => c.platform));

  // Builds the parent dropdown contents. Ordering (Scott's call):
  //   1. "(None — make this a root account)" option (rendered in the select)
  //   2. Brand-type accounts, with THIS account's tree-brand first when in
  //      edit mode, then the other brands alphabetically.
  //   3. All other candidate parents (groups, regions, head_offices,
  //      dealerships, locations) alphabetical by name.
  //
  // Cycle guard (unchanged): in edit mode, self + descendants are forbidden.
  const { brandOptions, otherOptions } = useMemo(() => {
    const all = allAccountsQuery.data ?? [];

    let candidates = all;
    let treeBrandId: string | null = null;
    if (mode.kind === 'edit') {
      const forbidden = new Set<string>([mode.account.id]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const a of all) {
          if (a.parentAccountId && forbidden.has(a.parentAccountId) && !forbidden.has(a.id)) {
            forbidden.add(a.id);
            grew = true;
          }
        }
      }
      candidates = all.filter((a) => !forbidden.has(a.id));

      // Walk upwards from this account's current parent to find the tree's
      // brand ancestor. That brand bubbles to the top of the Brand section so
      // operators don't hunt for it when the list is dominated by groups.
      const byId = new Map(all.map((a) => [a.id, a]));
      let cursor: string | null = mode.account.parentAccountId;
      const seen = new Set<string>();
      while (cursor !== null && !seen.has(cursor)) {
        seen.add(cursor);
        const node = byId.get(cursor);
        if (!node) break;
        if (node.accountType === 'brand') {
          treeBrandId = node.id;
          break;
        }
        cursor = node.parentAccountId;
      }
    }

    const brands = candidates.filter((a) => a.accountType === 'brand');
    const others = candidates.filter((a) => a.accountType !== 'brand');

    brands.sort((a, b) => {
      if (a.id === treeBrandId && b.id !== treeBrandId) return -1;
      if (b.id === treeBrandId && a.id !== treeBrandId) return 1;
      return a.name.localeCompare(b.name);
    });
    others.sort((a, b) => a.name.localeCompare(b.name));

    return { brandOptions: brands, otherOptions: others };
  }, [allAccountsQuery.data, mode]);

  // Round-5 (null=inherit): the LP editor renders an "Inherited from
  // brand" preview below each landing URL input. Resolving the
  // preview needs the parent brand's FranchiseProfile — walk up
  // parentAccountId from the form's live state (so changing the
  // parent dropdown re-targets the preview), then fetch the brand FP.
  const treeBrandIdLive: string | null = useMemo(() => {
    const all = allAccountsQuery.data ?? [];
    if (all.length === 0) return null;
    const byId = new Map(all.map((a) => [a.id, a]));
    let cursor: string | null = state.parentAccountId;
    const seen = new Set<string>();
    while (cursor !== null && !seen.has(cursor)) {
      seen.add(cursor);
      const node = byId.get(cursor);
      if (!node) return null;
      if (node.accountType === 'brand') return node.id;
      cursor = node.parentAccountId;
    }
    return null;
  }, [allAccountsQuery.data, state.parentAccountId]);

  // Only fetch when a location profile editor is actually being
  // authored — saves a query for brand / group / non-LP edits.
  const brandFranchiseProfileQuery = useQuery({
    queryKey: ['franchiseProfile', treeBrandIdLive],
    queryFn: () => getFranchiseProfile(treeBrandIdLive!),
    enabled:
      treeBrandIdLive !== null &&
      state.accountType === 'location' &&
      state.attachLp,
  });

  // Single chained mutation: first save the Account (create or update), then
  // optionally create-or-update the attached FranchiseProfile or
  // FranchiseLocationProfile. Each call is individually audited on the server;
  // a mid-sequence failure surfaces a clean error and leaves earlier writes in
  // place so the operator can retry without rolling back.
  const saveMut = useMutation({
    mutationFn: async (): Promise<Account> => {
      const accountInput = toInput(state);
      const account =
        mode.kind === 'create'
          ? await createAccount(accountInput)
          : await updateAccount(mode.account.id, accountInput);

      if (state.attachFp && account.accountType === 'brand') {
        const fpInput = fpToInput(account.id, state.fp);
        const existing = mode.kind === 'edit' ? mode.franchiseProfile : null;
        const { accountId: _aid, ...body } = fpInput;
        if (existing) {
          await updateFranchiseProfile(account.id, body);
        } else {
          await createFranchiseProfile(account.id, body);
        }
      }

      if (state.attachLp && account.accountType === 'location') {
        const lpInput = lpToInput(account.id, state.lp);
        const existing = mode.kind === 'edit' ? mode.franchiseLocationProfile : null;
        const { accountId: _aid, ...body } = lpInput;
        if (existing) {
          await updateFranchiseLocationProfile(account.id, body);
        } else {
          await createFranchiseLocationProfile(account.id, body);
        }
      }

      // 2026-07-24 — community_management now DERIVES communityEnabled server-side
      // (like runsMeta from meta_ads). When it flips false→true on this save, fire
      // the same scoped seed the old Manage-Accounts dialog fired, so the account's
      // recent comments appear without waiting for the ~20-min reconcile cron. The
      // server-returned account.communityEnabled IS the derived value; compare it
      // against the pre-edit flag (create ⇒ never previously enabled). Best-effort:
      // a seed failure must never block the successful save + navigation.
      const wasCommunityEnabled =
        mode.kind === 'edit' ? mode.account.communityEnabled ?? false : false;
      if (account.communityEnabled === true && !wasCommunityEnabled) {
        await seedAccount(account.id).catch(() => undefined);
      }

      return account;
    },
    onSuccess: async (account) => {
      await qc.invalidateQueries({ queryKey: ['accounts'] });
      await qc.invalidateQueries({ queryKey: ['account', account.id] });
      // The shared link-icons read (30-min stale) — an edited URL should show
      // on every surface now, not half an hour from now.
      await qc.invalidateQueries({ queryKey: ACCOUNT_LINKS_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: ['franchiseProfile', account.id] });
      await qc.invalidateQueries({ queryKey: ['franchiseLocationProfile', account.id] });
      navigate(`/accounts/${account.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (allAccountsQuery.isLoading) return <CenteredSpinner />;

  const submitting = saveMut.isPending;

  function setName(v: string) {
    setState((s) => ({
      ...s,
      name: v,
      slug: s.slugTouched ? s.slug : slugify(v),
    }));
  }
  function setSlug(v: string) {
    setState((s) => ({ ...s, slug: v, slugTouched: true }));
  }
  function toggleService(svc: Service) {
    setState((s) => {
      const next = new Set(s.services);
      if (next.has(svc)) next.delete(svc); else next.add(svc);
      return { ...s, services: next };
    });
  }
  function updateAddr<K extends keyof Address>(key: K, value: Address[K]) {
    setState((s) => ({ ...s, address: { ...s.address, [key]: value } }));
  }

  function updateFp<K extends keyof FranchiseProfileFormSlice>(key: K, value: FranchiseProfileFormSlice[K]) {
    setState((s) => ({ ...s, fp: { ...s.fp, [key]: value } }));
  }
  function updateLp<K extends keyof FranchiseLocationFormSlice>(key: K, value: FranchiseLocationFormSlice[K]) {
    setState((s) => ({ ...s, lp: { ...s.lp, [key]: value } }));
  }
  // Phase 2b — service config setters.
  function updateCc<K extends keyof ContentCreationFormSlice>(key: K, value: ContentCreationFormSlice[K]) {
    setState((s) => ({ ...s, contentCreation: { ...s.contentCreation, [key]: value } }));
  }
  function updateCcQuota(key: keyof ContentCreationFormSlice['quotas'], value: string) {
    setState((s) => ({
      ...s,
      contentCreation: {
        ...s.contentCreation,
        quotas: { ...s.contentCreation.quotas, [key]: value },
      },
    }));
  }
  function updateCm<K extends keyof CommunityManagementFormSlice>(key: K, value: CommunityManagementFormSlice[K]) {
    setState((s) => ({ ...s, communityManagement: { ...s.communityManagement, [key]: value } }));
  }
  function updateEscalation(role: EscalationRole, id: string | null) {
    setState((s) => ({
      ...s,
      communityManagement: {
        ...s.communityManagement,
        escalationContacts: { ...s.communityManagement.escalationContacts, [role]: id },
      },
    }));
  }
  function setLpSuburb(v: string) {
    setState((s) => ({
      ...s,
      lp: {
        ...s.lp,
        franchiseSuburb: v,
        franchiseSuburbSlug: s.lp.franchiseSuburbSlugTouched ? s.lp.franchiseSuburbSlug : slugify(v),
      },
    }));
  }
  function setLpSuburbSlug(v: string) {
    setState((s) => ({
      ...s,
      lp: { ...s.lp, franchiseSuburbSlug: v, franchiseSuburbSlugTouched: true },
    }));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    saveMut.mutate();
  }

  // Tab state persisted in the URL (?tab=…), mirroring the read-only view so a
  // view→edit hand-off keeps the tab. The editable tab set follows the CURRENT
  // form accountType (so the Franchise tab appears/disappears live in create
  // mode); a URL tab that isn't editable here (e.g. arriving from the view's
  // Contacts tab) resolves to the first editable tab.
  const editTabs = visibleEditTabIds(state.accountType);
  const activeTab = resolveAccountTab(searchParams.get('tab'), editTabs);
  const setTab = (id: AccountTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{mode.kind === 'create' ? 'New account' : `Edit: ${mode.account.name}`}</h1>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setTab(v as AccountTabId)}>
        <TabsList className="flex-wrap h-auto">
          {editTabs.map((id) => (
            <TabsTrigger key={id} value={id}>
              {accountTabLabel(id)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="details" className="space-y-6">
      <SectionBlock title="Identity & hierarchy">
        <Text label="Name" value={state.name} onChange={setName} required placeholder="Snap Fitness Perth City" />
        <Text label="Slug" value={state.slug} onChange={setSlug} required placeholder="snap-fitness-perth-city" />
        <div>
          <label className={labelClass}>Parent account</label>
          <Combobox
            aria-label="Parent account"
            searchPlaceholder="Search accounts…"
            value={state.parentAccountId ?? ''}
            onValueChange={(v) => {
              const newParentId = v === '' ? null : v;
              // In create mode, seed the currency from the chosen parent so
              // operators don't have to re-pick it. Edit mode preserves the
              // stored currency -- users who want to mirror parent currency
              // can change it manually.
              setState((s) => {
                const next: FormState = { ...s, parentAccountId: newParentId };
                if (mode.kind === 'create' && newParentId) {
                  const parent = (allAccountsQuery.data ?? []).find(
                    (a) => a.id === newParentId,
                  );
                  if (parent?.currency) next.currency = parent.currency;
                }
                return next;
              });
            }}
            options={[
              { value: '', label: '(None — make this a root account)' },
              ...brandOptions.map((a) => ({ value: a.id, label: a.name, description: 'Brand' })),
              ...otherOptions.map((a) => ({
                value: a.id,
                label: a.name,
                description: a.accountType,
                keywords: a.accountType,
              })),
            ]}
          />
        </div>
        <Select label="Account type" value={state.accountType} options={ACCOUNT_TYPES} onChange={(v) => setState((s) => ({ ...s, accountType: v }))} required />
        <Select label="Industry" value={state.industry} options={INDUSTRIES} onChange={(v) => setState((s) => ({ ...s, industry: v }))} required />
        <Select label="Status" value={state.status} options={STATUSES} onChange={(v) => setState((s) => ({ ...s, status: v }))} required />
        <Select
          label="Currency"
          value={state.currency || 'AUD'}
          options={CURRENCIES}
          onChange={(v) => setState((s) => ({ ...s, currency: v }))}
          required
        />
        <p className="text-xs text-slate-500 -mt-3">
          Every account carries its own currency. New accounts default from the selected parent; mixed-currency trees are allowed.
        </p>
        <div>
          <label className={labelClass}>Country</label>
          <SelectField
            aria-label="Country"
            value={state.country}
            onValueChange={(v) => setState((s) => ({ ...s, country: v }))}
            options={[
              { value: '', label: '— Not set —' },
              // Preserve a stored value that isn't in the canonical list so
              // editing never silently drops it.
              ...(state.country && !COUNTRIES.includes(state.country)
                ? [{ value: state.country, label: state.country }]
                : []),
              ...COUNTRIES.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>
        <p className="text-xs text-slate-500 -mt-3">
          Operating country for reporting. When left blank, the reporting dashboard seeds it from the currency.
        </p>
      </SectionBlock>

      <SectionBlock title="Commercial">
        <Select label="Media spend paid by" value={state.mediaSpendPaidBy} options={PAID_BY} onChange={(v) => setState((s) => ({ ...s, mediaSpendPaidBy: v }))} />
        <div>
          <label className={labelClass}>Account manager</label>
          {/* Preserve a stale/unknown uid so editing an account whose AM is no
              longer in the user list doesn't silently drop the assignment. */}
          <Combobox
            aria-label="Account manager"
            searchPlaceholder="Search users…"
            value={state.accountManagerId}
            onValueChange={(v) => setState((s) => ({ ...s, accountManagerId: v }))}
            options={[
              { value: '', label: '— Unassigned —' },
              ...(state.accountManagerId &&
              !(usersQuery.data ?? []).some((u) => u.uid === state.accountManagerId)
                ? [
                    {
                      value: state.accountManagerId,
                      label: accountManagerLabel(state.accountManagerId, []),
                    },
                  ]
                : []),
              ...(usersQuery.data ?? []).map((u) => ({
                value: u.uid,
                label: accountManagerLabel(u.uid, usersQuery.data ?? []),
                description: u.email,
                keywords: u.email,
              })),
            ]}
          />
        </div>
        <Text label="Salesforce account id" value={state.salesforceAccountId} onChange={(v) => setState((s) => ({ ...s, salesforceAccountId: v }))} />
      </SectionBlock>

      <SectionBlock title="Address & Contact Details">
        <Text label="Business phone" value={state.phone} onChange={(v) => setState((s) => ({ ...s, phone: v }))} />
        <Text label="Street" value={state.address.street} onChange={(v) => updateAddr('street', v)} />
        <Text label="Suburb" value={state.address.suburb} onChange={(v) => updateAddr('suburb', v)} />
        <Text label="State" value={state.address.state} onChange={(v) => updateAddr('state', v)} />
        <Text label="Postcode" value={state.address.postcode} onChange={(v) => updateAddr('postcode', v)} />
        <div>
          <label className={labelClass}>Country</label>
          <SelectField
            aria-label="Address country"
            value={state.address.country}
            onValueChange={(v) => updateAddr('country', v)}
            options={[
              { value: '', label: '— Not set —' },
              ...(state.address.country && !COUNTRIES.includes(state.address.country)
                ? [{ value: state.address.country, label: state.address.country }]
                : []),
              ...COUNTRIES.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>
      </SectionBlock>

      <SectionBlock title="Notes & content">
        <TextArea label="Description" value={state.description} onChange={(v) => setState((s) => ({ ...s, description: v }))} />
        <TextArea label="Targeting notes" value={state.targetingNotes} onChange={(v) => setState((s) => ({ ...s, targetingNotes: v }))} />
        <TextArea label="General notes" value={state.notes} onChange={(v) => setState((s) => ({ ...s, notes: v }))} />
        <TextArea label="Lead syncing notes" value={state.leadSyncingNotes} onChange={(v) => setState((s) => ({ ...s, leadSyncingNotes: v }))} />
      </SectionBlock>
        </TabsContent>

        <TabsContent value="services" className="space-y-6">
      <SectionBlock title="Services">
        <div>
          <label className={labelClass}>Services</label>
          {/* Phase 2a — services are the single control. Ticking meta_ads /
              google_ads reveals that platform's master budget below (and, in
              Phase 2b, content_creation / community_management reveal their
              config). The standalone Runs Meta / Runs Google flags are gone —
              they're now DERIVED from these checkboxes on save. */}
          <p className="text-xs text-slate-500 -mt-1 mb-2">
            Tick a service to enable it. Some services reveal extra configuration below.
          </p>
          <div className="flex flex-wrap gap-3">
            {SERVICES.map((s) => (
              <label key={s} className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={state.services.has(s)} onChange={() => toggleService(s)} />
                {serviceLabel(s)}
              </label>
            ))}
          </div>
        </div>

        {state.services.has('meta_ads') && (
          <div className="border-t border-slate-200 pt-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Meta Ads</h3>
            <Text
              label="Meta master monthly budget (integer dollars; stored as cents)"
              value={state.metaBudget}
              onChange={(v) => setState((s) => ({ ...s, metaBudget: v }))}
              placeholder="500.00"
            />
          </div>
        )}

        {state.services.has('google_ads') && (
          <div className="border-t border-slate-200 pt-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-800">Google Ads</h3>
            <Text
              label="Google master monthly budget (integer dollars; stored as cents)"
              value={state.googleBudget}
              onChange={(v) => setState((s) => ({ ...s, googleBudget: v }))}
              placeholder="500.00"
            />
          </div>
        )}
      </SectionBlock>

      {/* Phase 2b — Content Creation config, revealed when the service is ticked.
          Quota inputs appear per relevant channel (Facebook Feed with Facebook,
          Instagram Feed with Instagram, Stories with FB or IG, Google Business
          with Google); the media-budget spends are gated the same way. */}
      {state.services.has('content_creation') && (
        <SectionBlock title="Content Creation">
          <ChannelTogglesField
            label="Channels"
            value={state.contentCreation.channels}
            onChange={(v) => updateCc('channels', v)}
          />

          <div className="border-t border-slate-200 pt-4">
            <label className={labelClass}>Monthly post quotas</label>
            {!state.contentCreation.channels.facebook &&
            !state.contentCreation.channels.instagram &&
            !state.contentCreation.channels.google ? (
              <p className="text-xs text-slate-500">Turn on a channel above to set its quota.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {state.contentCreation.channels.facebook && (
                  <Text
                    label="Facebook Feed"
                    value={state.contentCreation.quotas.facebookFeed}
                    onChange={(v) => updateCcQuota('facebookFeed', v)}
                    placeholder="e.g. 8"
                  />
                )}
                {state.contentCreation.channels.instagram && (
                  <Text
                    label="Instagram Feed"
                    value={state.contentCreation.quotas.instagramFeed}
                    onChange={(v) => updateCcQuota('instagramFeed', v)}
                    placeholder="e.g. 8"
                  />
                )}
                {(state.contentCreation.channels.facebook ||
                  state.contentCreation.channels.instagram) && (
                  <Text
                    label="Stories"
                    value={state.contentCreation.quotas.stories}
                    onChange={(v) => updateCcQuota('stories', v)}
                    placeholder="e.g. 12"
                  />
                )}
                {state.contentCreation.channels.google && (
                  <Text
                    label="Google Business"
                    value={state.contentCreation.quotas.googleBusiness}
                    onChange={(v) => updateCcQuota('googleBusiness', v)}
                    placeholder="e.g. 4"
                  />
                )}
              </div>
            )}
          </div>

          {(state.contentCreation.channels.facebook ||
            state.contentCreation.channels.instagram) && (
            <div className="border-t border-slate-200 pt-4">
              <label className={labelClass}>
                Content media budget (integer dollars; stored as cents)
              </label>
              <div className="grid grid-cols-2 gap-4">
                {state.contentCreation.channels.facebook && (
                  <Text
                    label="Facebook Spend"
                    value={state.contentCreation.facebookSpend}
                    onChange={(v) => updateCc('facebookSpend', v)}
                    placeholder="500.00"
                  />
                )}
                {state.contentCreation.channels.instagram && (
                  <Text
                    label="Instagram Spend"
                    value={state.contentCreation.instagramSpend}
                    onChange={(v) => updateCc('instagramSpend', v)}
                    placeholder="500.00"
                  />
                )}
              </div>
            </div>
          )}

          <Text
            label="Content sharing group"
            value={state.contentCreation.contentSharingGroup}
            onChange={(v) => updateCc('contentSharingGroup', v)}
          />

          {/* Sales / Service contact-details split (2026-08-05). Both render for
              EVERY account type — the caption-wildcard vocabulary is uniform, so a
              gym just leaves Service blank. */}
          <div className="border-t border-slate-200 pt-4">
            <TextArea
              label="Sales contact details for post"
              value={state.contentCreation.salesContactDetailsForPost}
              onChange={(v) => updateCc('salesContactDetailsForPost', v)}
              rows={4}
            />
            <p className="mt-1 text-xs text-slate-500">
              Free-text block (address / phone / URL, emoji welcome). Inserted into a
              caption by the <code>{'{sales_contact_details}'}</code> wildcard.
            </p>
          </div>

          <div>
            <TextArea
              label="Service contact details for post"
              value={state.contentCreation.serviceContactDetailsForPost}
              onChange={(v) => updateCc('serviceContactDetailsForPost', v)}
              rows={4}
            />
            <p className="mt-1 text-xs text-slate-500">
              The service department's equivalent — inserted by{' '}
              <code>{'{service_contact_details}'}</code>. Leave blank if the account has
              no separate service contact.
            </p>
          </div>

          <TextArea
            label="Content creation notes"
            value={state.contentCreation.notes}
            onChange={(v) => updateCc('notes', v)}
            rows={4}
          />
        </SectionBlock>
      )}

      {/* Phase 2b — Community Management config, revealed when ticked. The
          escalation-contact ROLES vary by account type (dealership →
          Primary/Sales/Service; others → Primary/Back Up) via the shared
          escalationRolesForAccountType helper. */}
      {state.services.has('community_management') && (
        <SectionBlock title="Community Management">
          <ChannelTogglesField
            label="Channels managed"
            value={state.communityManagement.channels}
            onChange={(v) => updateCm('channels', v)}
          />
          <Text
            label="Default response sign-off person"
            value={state.communityManagement.signOffPerson}
            onChange={(v) => updateCm('signOffPerson', v)}
            placeholder="The Team at Liverpool Mazda"
          />
          <div className="border-t border-slate-200 pt-4">
            <label className={labelClass}>Escalation contacts</label>
            <p className="mt-1 mb-2 text-xs text-slate-500">
              Roles depend on the account type. Link an existing contact of this account or
              create a new one.
            </p>
            <div className="space-y-3">
              {escalationRolesForAccountType(state.accountType).map((role) => (
                <EscalationContactPicker
                  key={role}
                  accountId={mode.kind === 'edit' ? mode.account.id : ''}
                  label={escalationRoleLabel(role)}
                  value={state.communityManagement.escalationContacts[role]}
                  onChange={(id) => updateEscalation(role, id)}
                />
              ))}
            </div>
          </div>
        </SectionBlock>
      )}

      {state.services.has('meta_aia') && (
        <SectionBlock title="Meta AIA (Automated Inventory Ads)">
          <Text
            label="Monthly budget"
            value={state.metaAia.budget}
            onChange={(v) => setState((s) => ({ ...s, metaAia: { ...s.metaAia, budget: v } }))}
          />
          <Text
            label="Feed URL"
            value={state.metaAia.feedUrl}
            onChange={(v) => setState((s) => ({ ...s, metaAia: { ...s.metaAia, feedUrl: v } }))}
          />
        </SectionBlock>
      )}

      {state.services.has('google_vla') && (
        <SectionBlock title="Google VLA (Vehicle Listing Ads)">
          <Text
            label="Monthly budget"
            value={state.googleVla.budget}
            onChange={(v) => setState((s) => ({ ...s, googleVla: { ...s.googleVla, budget: v } }))}
          />
          <Text
            label="Feed URL"
            value={state.googleVla.feedUrl}
            onChange={(v) => setState((s) => ({ ...s, googleVla: { ...s.googleVla, feedUrl: v } }))}
          />
        </SectionBlock>
      )}
        </TabsContent>

        <TabsContent value="connections" className="space-y-6">
      <SectionBlock title="Ad accounts & URLs">
        <Text label="Meta ad account id" value={state.fbAdAccountId} onChange={(v) => setState((s) => ({ ...s, fbAdAccountId: v }))} />
        {mode.kind === 'edit' &&
          !!mode.account.zernioProfileId &&
          state.fbAdAccountId.trim() !== (mode.account.fbAdAccountId ?? '') && (
            <p className="-mt-1 flex items-start gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Changing the ad account doesn't re-scope the Zernio connection — reconnect (account
              page → API Connections) for ads to publish to the new account.
            </p>
          )}
        <Text
          label="Meta verified advertiser ID"
          value={state.metaVerifiedAdvertiserId}
          onChange={(v) => setState((s) => ({ ...s, metaVerifiedAdvertiserId: v }))}
        />
        <p className="-mt-1 text-xs text-muted-foreground">
          Needed for some Australian ad accounts. Business Settings &gt; Verified identities. Used as
          both beneficiary and payer.
        </p>
        {parseVerifiedAdvertiserId(state.metaVerifiedAdvertiserId).ok === false && (
          <p className="-mt-1 text-xs text-destructive">{VERIFIED_ADVERTISER_ID_MESSAGE}</p>
        )}
        <Text label="Meta ad account URL" value={state.fbAdAccountUrl} onChange={(v) => setState((s) => ({ ...s, fbAdAccountUrl: v }))} />
        <Text
          label="Instagram account ID"
          value={state.defaultInstagramAccountId}
          onChange={(v) => setState((s) => ({ ...s, defaultInstagramAccountId: v }))}
          placeholder="17841…"
          hint="Optional. Used for ads with Story creative or per-placement assets. Find the ID via Meta Business Settings → Accounts → Instagram Accounts (if linked through Business Manager) OR via the Facebook Page → Settings → Linked Accounts → Instagram (if linked at Page level)."
        />
        <Text
          label="Instagram account name"
          value={state.defaultInstagramAccountName}
          onChange={(v) => setState((s) => ({ ...s, defaultInstagramAccountName: v }))}
          placeholder="@snapfitnessmountdruitt"
          hint="Display label only."
        />
        {/* Feature A — Meta Ad Builder defaults. Live pickers when the account is
            connected (linked Zernio profile + ad account), else manual id entry. */}
        <DefaultResourceSelect
          label="Default conversion pixel"
          hint="Pre-fills the pixel selector on new Meta ad drafts (editable per draft)."
          value={state.defaultPixelId}
          onChange={(id, name) =>
            setState((s) => ({ ...s, defaultPixelId: id, defaultPixelName: name ?? '' }))
          }
          enabled={metaConnected}
          accountId={metaConnected ? mode.account.id : ''}
          resource="pixels"
        />
        <DefaultResourceSelect
          label="Default lead form"
          hint="Pre-fills the lead form on new instant-form drafts (editable per draft)."
          value={state.defaultLeadFormId}
          onChange={(id, name) =>
            setState((s) => ({ ...s, defaultLeadFormId: id, defaultLeadFormName: name ?? '' }))
          }
          enabled={metaConnected}
          accountId={metaConnected ? mode.account.id : ''}
          resource="lead-forms"
        />
        <Text label="Google Ads customer id" value={state.googleAdsCustomerId} onChange={(v) => setState((s) => ({ ...s, googleAdsCustomerId: v }))} placeholder="XXX-XXX-XXXX" />
        <Text label="Google Ads URL" value={state.googleAdsUrl} onChange={(v) => setState((s) => ({ ...s, googleAdsUrl: v }))} />
        <Text label="CRM ID" value={state.crmId} onChange={(v) => setState((s) => ({ ...s, crmId: v }))} hint="the club's id in the client's own CRM — Snap: the GymSales company id" />
        <Text label="ClubWise code" value={state.clubwiseCode} onChange={(v) => setState((s) => ({ ...s, clubwiseCode: v.toUpperCase() }))} hint="Anytime Fitness: the club's member-number prefix, 2-6 uppercase letters or digits. Unique per account." />
        {/* §3b Shared Ad Accounts (Meta OR Google) — the match-token chips appear
            whenever the roster shows this account IS shared on either platform, or
            the checkbox is ticked. The checkbox stays a manual override for the
            pre-sharing case (ticked before the second sibling exists); it is NOT
            what gates the field, so a Google-shared location can reach the tokens
            without ticking a box describing a situation it isn't in. Counts read
            the ground truth off the roster (no re-pull needed to change tokens).
            PLACEMENT: deliberately BELOW both platforms' ad-account id/URL fields
            (it used to sit among the Meta ones, which read as Meta-only). Mirrored
            in the same position on AccountDetail's read view. */}
        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-sm font-semibold text-slate-900">Shared ad account attribution</h3>
          <label className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={state.isSharedAdAccount}
              onChange={(e) => setState((s) => ({ ...s, isSharedAdAccount: e.target.checked }))}
            />
            Part of a shared ad account (Meta or Google)
          </label>
          <p className="mt-1 text-xs text-slate-500">
            Tick when several locations share ONE Meta ad account or ONE Google Ads account. The
            match tokens below attribute this location's campaigns on <strong>both</strong> Meta
            and Google — one list serves the two platforms.
          </p>
          {sharedClusters.map((c) => (
            <p key={c.platform} className="mt-1 text-xs text-blue-600">
              This {c.platform === 'meta' ? 'Meta ad account' : 'Google Ads account'} is shared by{' '}
              {c.count} Kyck accounts — {c.tokensSet}{' '}
              {c.tokensSet === 1 ? 'has' : 'have'} tokens set.
              {c.tokensSet < c.count &&
                ' Set tokens so only this location’s campaigns are attributed here.'}
            </p>
          ))}
          {shouldShowMatchTokens(sharedClusters, state.isSharedAdAccount) && (
            <div className="mt-3">
              <label className={labelClass}>Match tokens</label>
              <TokenChipInput
                value={state.adAccountMatchTokens}
                onChange={(tokens) => setState((s) => ({ ...s, adAccountMatchTokens: tokens }))}
              />
              <p className="mt-1 text-xs text-slate-500">
                Use the suburb/location name (the shortest distinctive part), e.g.{' '}
                <span className="font-mono">Rosebay</span> — add variants like{' '}
                <span className="font-mono">Rose Bay</span> as failsafes. Campaigns whose name
                contains any token (case-insensitive) are attributed to this location
                {detectedShared ? ` on ${sharedOnLabel}` : ''}.
              </p>
            </div>
          )}
        </div>
        <Text label="Google Analytics property id" value={state.googleAnalyticsPropertyId} onChange={(v) => setState((s) => ({ ...s, googleAnalyticsPropertyId: v }))} />
        <Text label="Facebook page URL" value={state.facebookPageUrl} onChange={(v) => setState((s) => ({ ...s, facebookPageUrl: v }))} />
        <Text label="Instagram URL" value={state.instagramUrl} onChange={(v) => setState((s) => ({ ...s, instagramUrl: v }))} />
        <Text label="TikTok URL" value={state.tiktokUrl} onChange={(v) => setState((s) => ({ ...s, tiktokUrl: v }))} />
        <Text label="Google Business Profile URL" value={state.googleBusinessProfileUrl} onChange={(v) => setState((s) => ({ ...s, googleBusinessProfileUrl: v }))} />
        <Text label="Website URL" value={state.websiteUrl} onChange={(v) => setState((s) => ({ ...s, websiteUrl: v }))} />
        <Text label="Report dashboard URL" value={state.reportUrl} onChange={(v) => setState((s) => ({ ...s, reportUrl: v }))} />
        {/* Build A — enrol this account in the recurring Meta reporting pull that
            feeds the reporting dashboard's BigQuery warehouse. */}
        <div className="border-t border-slate-200 pt-4">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={state.reportingSyncEnabled}
              onChange={(e) => setState((s) => ({ ...s, reportingSyncEnabled: e.target.checked }))}
            />
            Include in reporting sync (recurring Meta pull)
          </label>
          <p className="mt-1 text-xs text-slate-500">
            When on, the scheduled reporting job pulls this account's Meta performance into the
            warehouse. Off by default; can also be set in bulk from the accounts grid.
          </p>
        </div>
        {state.accountType === 'brand' && (
          <div className="border-t border-slate-200 pt-4">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={state.portalMetaGoalReport}
                onChange={(e) => setState((s) => ({ ...s, portalMetaGoalReport: e.target.checked }))}
              />
              Client report: split Meta results by campaign goal
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Every location under this brand gets a Meta page with lead gen, purchase, other and
              uncategorised campaigns reported separately, instead of one combined conversions
              figure. New locations added under the brand inherit it.
            </p>
          </div>
        )}
        <ReportTemplateField
          value={state.reportTemplate}
          onChange={(v) => setState((s) => ({ ...s, reportTemplate: v }))}
          draft={{
            id: mode.kind === 'edit' ? mode.account.id : null,
            parentAccountId: state.parentAccountId || null,
            brandAccountId: state.brandAccountId || null,
            industry: state.industry,
          }}
          roster={allAccountsQuery.data ?? []}
        />
        <Text label="Client Drive URL" value={state.clientDriveUrl} onChange={(v) => setState((s) => ({ ...s, clientDriveUrl: v }))} />
        <Text label="Internal Drive URL" value={state.internalDriveUrl} onChange={(v) => setState((s) => ({ ...s, internalDriveUrl: v }))} />
        <Text label="GTM container id" value={state.gtmContainerId} onChange={(v) => setState((s) => ({ ...s, gtmContainerId: v }))} />
        <Text label="Tracking doc URL" value={state.trackingDocUrl} onChange={(v) => setState((s) => ({ ...s, trackingDocUrl: v }))} />
        <Text label="Zapier folder URL" value={state.zapierFolderUrl} onChange={(v) => setState((s) => ({ ...s, zapierFolderUrl: v }))} />
      </SectionBlock>
        </TabsContent>

        <TabsContent value="franchise" className="space-y-6">
      {state.accountType === 'brand' && (
        <SectionBlock title="Franchise profile (brand-level templates)">
          {mode.kind === 'edit' && mode.franchiseProfile !== null ? (
            <p className="text-xs text-slate-500">
              Editing the attached franchise profile. Saved in the same submit as the account.
            </p>
          ) : (
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state.attachFp}
                onChange={(e) => setState((s) => ({ ...s, attachFp: e.target.checked }))}
              />
              {mode.kind === 'create'
                ? 'Also attach a franchise profile now'
                : 'Create a franchise profile for this brand'}
            </label>
          )}

          {state.attachFp && (
            <>
              {/* Round 4 (Fix 2): demoted from required. AM may save a
                  partial FP and author clubNamePrefix later — xlsx import
                  will still warn on a missing prefix, so the gap is
                  caught at the actual point of use rather than blocking
                  brand creation. */}
              <Text
                label="Club name prefix"
                value={state.fp.clubNamePrefix}
                onChange={(v) => updateFp('clubNamePrefix', v)}
                placeholder="Snap Fitness"
              />
              <p className="text-xs text-slate-500 -mt-3">
                Prepended to each location's franchise suburb during xlsx import to form the location's name (e.g. "Snap Fitness" + "Perth City" → "Snap Fitness Perth City").
              </p>

              <Text
                label="Default Google path1"
                value={state.fp.defaultGooglePath1}
                onChange={(v) => updateFp('defaultGooglePath1', v)}
                placeholder="gym"
              />

              <div>
                <label className={labelClass}>Default Google boilerplate headlines</label>
                <div className="space-y-2">
                  {state.fp.headlines.map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className={inputClass}
                        value={h}
                        onChange={(e) =>
                          updateFp(
                            'headlines',
                            state.fp.headlines.map((x, j) => (j === i ? e.target.value : x)),
                          )
                        }
                        placeholder="{suburb} Gym — Join Today"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          updateFp('headlines', state.fp.headlines.filter((_, j) => j !== i))
                        }
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => updateFp('headlines', [...state.fp.headlines, ''])}
                  >
                    + Add headline
                  </Button>
                </div>
                <PlaceholderHint />
              </div>

              {/* Round 4 (Fix 2): landing-page templates demoted from
                  required. Brand FP can be saved partial; AM authors
                  templates later. Server zod relaxed to match. */}
              <div>
                <Text
                  label="Landing page template — offer"
                  value={state.fp.landingOffer}
                  onChange={(v) => updateFp('landingOffer', v)}
                  placeholder="https://snapfitness.com.au/{suburbSlug}-offer"
                />
                <PlaceholderHint />
              </div>
              <div>
                <Text
                  label="Landing page template — sale"
                  value={state.fp.landingSale}
                  onChange={(v) => updateFp('landingSale', v)}
                  placeholder="https://snapfitness.com.au/{suburbSlug}-sale"
                />
                <PlaceholderHint />
              </div>
              <div>
                <Text
                  label="Landing page template — location"
                  value={state.fp.landingLocation}
                  onChange={(v) => updateFp('landingLocation', v)}
                  placeholder="https://snapfitness.co.nz/{suburbSlug}"
                />
                <PlaceholderHint />
              </div>

              <Text
                label="Default ad copy doc URL"
                value={state.fp.defaultAdCopyDocUrl}
                onChange={(v) => updateFp('defaultAdCopyDocUrl', v)}
              />
              <Text
                label="Brand assets folder URL"
                value={state.fp.brandAssetsUrl}
                onChange={(v) => updateFp('brandAssetsUrl', v)}
              />
              <TextArea
                label="Brand portal notes"
                value={state.fp.brandPortalNotes}
                onChange={(v) => updateFp('brandPortalNotes', v)}
                rows={4}
              />
              <div>
                <TextArea
                  label="Default gym info (brand-level fallback for {gymInfo})"
                  value={state.fp.defaultGymInfo}
                  onChange={(v) => updateFp('defaultGymInfo', v)}
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Used when a location's own gymInfo is blank. Multi-line; emoji-preserving.
                </p>
              </div>
              {/* ⚠️⚠️ THE BRAND VOICE PROFILE (2026-08-26). Authored ONCE here and
                  read by every AI copy surface — post captions and Franchise
                  Campaign copy — so a brand's voice can never mean two different
                  things on two screens. The copy below says what it is FOR and,
                  just as importantly, what it cannot do: it is context the model
                  follows, never permission to make a claim the rules forbid. */}
              <div>
                <TextArea
                  label="Brand voice profile (used by AI copy drafting)"
                  value={state.fp.brandVoiceProfile}
                  onChange={(v) => updateFp('brandVoiceProfile', v)}
                  rows={8}
                  placeholder={
                    'How this brand sounds, what it sells, and what it never says. ' +
                    'e.g. Warm and local, never corporate. We talk about the community, ' +
                    'not the equipment. Members are "members", never "customers". ' +
                    'We never discount publicly and we never compare ourselves to other gyms.'
                  }
                />
                <p className="text-xs text-slate-500 mt-1">
                  Read by AI copy drafting on posts <strong>and</strong> campaigns — write it
                  once and both follow it. Say how the brand sounds, what it sells, the words
                  it uses and the ones it avoids.{' '}
                  <strong className="font-medium">
                    It guides the writing, it can&apos;t change what may be claimed
                  </strong>{' '}
                  — asking for &ldquo;best prices in Australia&rdquo; here still won&apos;t
                  produce one, because unsubstantiated superlatives stay blocked. Up to{' '}
                  {MAX_BRAND_VOICE_PROFILE_CHARS.toLocaleString()} characters; it rides every
                  request, so keep it a page rather than a manual.
                </p>
                {/* ⚠️ Only on a saved brand — the drafter reads that brand's
                    campaigns, and there are none before it exists. */}
                {mode.kind === 'edit' && state.accountType === 'brand' && (
                  <BrandVoiceDrafter
                    accountId={mode.account.id}
                    hasProfileText={state.fp.brandVoiceProfile.trim() !== ''}
                    onProposed={(draft) =>
                      setState((prev) => ({
                        ...prev,
                        fp: {
                          ...prev.fp,
                          brandVoiceProfile: draft.profile,
                          // ⚠️ PROVENANCE, stamped here and saved with the field, so
                          // a profile drafted a year ago from six campaigns is
                          // visibly old rather than silently authoritative.
                          brandVoiceProfileDraftedAt: new Date().toISOString(),
                        },
                      }))
                    }
                  />
                )}
                {state.fp.brandVoiceProfileDraftedAt && (
                  <p className="text-xs text-slate-500 mt-1">
                    AI-drafted from past copy on{' '}
                    {new Date(state.fp.brandVoiceProfileDraftedAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    , then edited by hand if you changed it since.
                  </p>
                )}
              </div>

              {/* ⚠️⚠️ ANTI-EXAMPLES. Unlike good examples these carry NO copying
                  risk — the model is told to avoid them, so it cannot lift their
                  angle — and they teach more, because a reason generalises where
                  a good ad only demonstrates. */}
              <div>
                <TextArea
                  label="Copy we've rejected, and why (used by AI copy drafting)"
                  value={state.fp.brandCopyAntiExamples}
                  onChange={(v) => updateFp('brandCopyAntiExamples', v)}
                  rows={5}
                  placeholder={
                    'Lines this client killed, and the reason. e.g. "Transform your body in 6 weeks" ' +
                    '— promises a result we cannot guarantee. "Best gym in Brisbane" — unsubstantiated. ' +
                    'Anything opening with "Are you tired of…" — the client hates it.'
                  }
                />
                <p className="text-xs text-slate-500 mt-1">
                  Given to the AI as{' '}
                  <strong className="font-medium">boundaries, never as examples to match</strong> —
                  so unlike good copy, these carry no risk of being imitated. The{' '}
                  <em>reason</em> is the useful part: it usually generalises into a rule about the
                  brand. Up to {MAX_BRAND_ANTI_EXAMPLES_CHARS.toLocaleString()} characters.
                </p>
              </div>
              <div>
                <Text
                  label="Default Meta campaign name template"
                  value={state.fp.defaultMetaCampaignNameTemplate}
                  onChange={(v) => updateFp('defaultMetaCampaignNameTemplate', v)}
                  placeholder="{locationName} - {campaignDescriptor} - {monthYear}"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Brand default for the Meta campaign name shown to media buyers.
                  Per-Campaign override available on the Campaign editor. Supports{' '}
                  <code className="font-mono">{'{locationName}'}</code>,{' '}
                  <code className="font-mono">{'{campaignDescriptor}'}</code>,{' '}
                  <code className="font-mono">{'{monthYear}'}</code>, plus the standard
                  location/brand placeholders.
                </p>
              </div>
              {/* Pre-P2b-3 polish round 5 §2.2: brand-default Google
                  ad-label template, parallel to the Meta default above. */}
              <div>
                <Text
                  label="Default Google ad label template"
                  value={state.fp.defaultGoogleAdLabelTemplate}
                  onChange={(v) => updateFp('defaultGoogleAdLabelTemplate', v)}
                  placeholder="{campaignDescriptor} - {monthYear}"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Brand default for the Google ad label media buyers paste into Google
                  Ads. Per-Campaign override available on the Campaign editor. Same
                  placeholder set as the Meta template above.
                </p>
              </div>
              {/* OrganicAssets §6.4: Drive Rollout Parent Folder. Edit mode
                  gets the validate-against-Drive control (keyed by the saved
                  account id); create mode gets a plain input that persists
                  the raw value with the rest of the profile — the AM
                  validates afterwards by editing. */}
              {mode.kind === 'edit' ? (
                <BrandDriveSettings
                  accountId={mode.account.id}
                  value={state.fp.driveRolloutParentFolderId}
                  onChange={(v) => updateFp('driveRolloutParentFolderId', v)}
                />
              ) : (
                <div>
                  <Text
                    label="Drive Rollout Parent Folder"
                    value={state.fp.driveRolloutParentFolderId}
                    onChange={(v) => updateFp('driveRolloutParentFolderId', v)}
                    placeholder="Paste the folder share link or ID…"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Parent folder under which Rollout asset folders are auto-created.
                    Must be shared with the Kyck service account as Editor (see
                    docs/Brand_Drive_Setup.md). Validate it after saving by editing
                    this brand.
                  </p>
                </div>
              )}
            </>
          )}
        </SectionBlock>
      )}

      {state.accountType === 'location' && (
        <SectionBlock title="Franchise location profile">
          {mode.kind === 'edit' && mode.franchiseLocationProfile !== null ? (
            <p className="text-xs text-slate-500">
              Editing the attached location profile. Saved in the same submit as the account.
            </p>
          ) : (
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state.attachLp}
                onChange={(e) => setState((s) => ({ ...s, attachLp: e.target.checked }))}
              />
              {mode.kind === 'create'
                ? 'Also attach a franchise location profile now'
                : 'Create a franchise location profile for this location'}
            </label>
          )}

          {state.attachLp && (
            <>
              <Text
                label="Franchise suburb"
                value={state.lp.franchiseSuburb}
                onChange={setLpSuburb}
                required
                placeholder="Perth City"
              />
              <Text
                label="Short suburb (for char-capped ad slots)"
                value={state.lp.franchiseSuburbShort}
                onChange={(v) => updateLp('franchiseSuburbShort', v)}
                placeholder="Perth"
              />
              <Text
                label="Franchise suburb slug"
                value={state.lp.franchiseSuburbSlug}
                onChange={setLpSuburbSlug}
                required
                placeholder="perth-city"
              />
              <Text
                label="State"
                value={state.lp.state}
                onChange={(v) => updateLp('state', v)}
                required
                placeholder="WA"
              />
              <Select
                label="Default form type"
                value={state.lp.defaultFormType}
                options={FORM_TYPES}
                onChange={(v) => updateLp('defaultFormType', v)}
                required
              />
              {/* Pre-P2b-3 polish round 5 §1: location-level Meta ad
                  type default. Inline rendering rather than the
                  generic Select component because the option values
                  ('no-default' / 'traffic' / 'lead_gen') need
                  friendlier display labels. Hint underneath explains
                  when the default applies. */}
              <div>
                <label className={labelClass}>Default Meta ad type</label>
                <SelectField
                  aria-label="Default Meta ad type"
                  value={state.lp.defaultMetaAdType}
                  onValueChange={(v) =>
                    updateLp(
                      'defaultMetaAdType',
                      v as 'no-default' | 'traffic' | 'lead_gen',
                    )
                  }
                  options={DEFAULT_META_AD_TYPES.map((v) => ({
                    value: v,
                    label: DEFAULT_META_AD_TYPE_LABEL[v],
                  }))}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Applies when the assigned Campaign's Meta ad type is "both" and the
                  row hasn't picked one. Set to "No default" to require a per-row pick.
                </p>
              </div>
              {/* Round 4 (Fix 2): per-location offer / sale landing pages
                  demoted from required. LP-mandatory set is
                  Suburb / Slug / State only — those drive templating and
                  failing-to-author them silently breaks Meta naming.
                  Landing pages can be filled later or inherited from
                  the brand-level template (round 5 wired the resolver
                  to use this LP value when set, otherwise the brand
                  template). */}
              {(() => {
                const lpAccount = { name: state.name, slug: state.slug };
                const lpStandIn = lpStandInFromForm(
                  state.lp,
                  mode.kind === 'edit' ? mode.account.id : 'new',
                );
                const brandTemplate = brandFranchiseProfileQuery.data?.landingPageTemplate ?? null;
                return (
                  <>
                    <div>
                      <Text
                        label="Offer landing page"
                        value={state.lp.offerLandingPage}
                        onChange={(v) => updateLp('offerLandingPage', v)}
                        placeholder="https://snapfitness.com.au/perth-city-offer"
                      />
                      <InheritedFromBrand
                        template={brandTemplate?.offer}
                        account={lpAccount}
                        locationStandIn={lpStandIn}
                        inputValue={state.lp.offerLandingPage}
                        onRevert={() => updateLp('offerLandingPage', '')}
                      />
                    </div>
                    <div>
                      <Text
                        label="Sale landing page"
                        value={state.lp.saleLandingPage}
                        onChange={(v) => updateLp('saleLandingPage', v)}
                      />
                      <InheritedFromBrand
                        template={brandTemplate?.sale}
                        account={lpAccount}
                        locationStandIn={lpStandIn}
                        inputValue={state.lp.saleLandingPage}
                        onRevert={() => updateLp('saleLandingPage', '')}
                      />
                    </div>
                    <div>
                      <Text
                        label="Location landing page"
                        value={state.lp.locationLandingPage}
                        onChange={(v) => updateLp('locationLandingPage', v)}
                      />
                      <InheritedFromBrand
                        template={brandTemplate?.location}
                        account={lpAccount}
                        locationStandIn={lpStandIn}
                        inputValue={state.lp.locationLandingPage}
                        onRevert={() => updateLp('locationLandingPage', '')}
                      />
                    </div>
                  </>
                );
              })()}
              <Text
                label="Package"
                value={state.lp.package}
                onChange={(v) => updateLp('package', v)}
                placeholder="Full Stack / Brand / Image"
              />
              <div>
                <label className={labelClass}>Club opened</label>
                <input
                  type="date"
                  className={inputClass}
                  value={state.lp.clubOpenedAt}
                  onChange={(e) => updateLp('clubOpenedAt', e.target.value)}
                />
              </div>
              <TextArea
                label="Gym info (USP block)"
                value={state.lp.gymInfo}
                onChange={(v) => updateLp('gymInfo', v)}
                rows={5}
              />
            </>
          )}
        </SectionBlock>
      )}

      </TabsContent>

        {/* Dealership — type-scoped tab (shown only for dealership accounts via
            visibleEditTabIds). brandAccountId picks the marque (a brand-type
            account); the resolver gives it precedence over the parent walk. */}
        {state.accountType === 'dealership' && (
          <TabsContent value="dealership" className="space-y-6">
            <SectionBlock title="Dealership">
              <div>
                <label className={labelClass}>Brand (marque)</label>
                <Combobox
                  aria-label="Brand marque"
                  searchPlaceholder="Search brands…"
                  value={state.brandAccountId}
                  onValueChange={(v) => setState((s) => ({ ...s, brandAccountId: v }))}
                  options={[
                    { value: '', label: '— Not set —' },
                    ...brandOptions.map((a) => ({ value: a.id, label: a.name })),
                  ]}
                />
                <p className="mt-1 text-xs text-slate-500">
                  The vehicle marque (a brand-type account, e.g. Mazda). Resolves this
                  dealership to its brand across the grid + reporting, taking precedence
                  over the parent group.
                </p>
              </div>
              <Text
                label="Website platform"
                value={state.dealershipWebsitePlatform}
                onChange={(v) => setState((s) => ({ ...s, dealershipWebsitePlatform: v }))}
              />
              <Text
                label="CRM system"
                value={state.dealershipCrmSystem}
                onChange={(v) => setState((s) => ({ ...s, dealershipCrmSystem: v }))}
              />
            </SectionBlock>
          </TabsContent>
        )}
      </Tabs>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : mode.kind === 'create' ? 'Create account' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
      </div>
    </form>
  );
}
